import { Injectable, inject } from '@angular/core';
import { OwnedCacheService } from './owned-cache.service';

/**
 * Last-known-good subscription state, used to decide what to do when the billing
 * API cannot be reached.
 *
 * THE PROBLEM THIS SOLVES
 * `OnboardingGuard` has to answer "may this user into the app?" before the dashboard
 * renders. Two failure modes pull in opposite directions:
 *
 *   - Fail CLOSED on any billing hiccup and a paying customer gets bounced to
 *     /select-plan during a transient outage. That was a real bug.
 *   - Fail OPEN on any billing hiccup and anyone who can stall the request — devtools
 *     throttling, a firewall DROP, a hosts-file blackhole — walks straight in. A
 *     churned customer could keep using the product indefinitely.
 *
 * Neither is acceptable, because "we could not read the subscription" is not one
 * situation. It is two: a customer we have previously seen entitled, and a user we
 * have never seen entitled at all. This records the former so the guard can tell
 * them apart, and grants a bounded grace window only to the first.
 *
 * The record is written through OwnedCacheService, so it is bound to the Cognito
 * subject and cannot be inherited by a different user on a shared browser.
 *
 * NOT A SECURITY BOUNDARY. A client-side guard is bypassable by anyone willing to
 * edit the running page; this raises the bar and stops an outage from silently
 * unlocking the product for everyone. Server-side entitlement checks remain the
 * only real gate.
 */

export interface SubscriptionGraceRecord {
    canAccessPlatform: boolean;
    status: string;
    companyId: string;
}

/**
 * How long a previously-entitled customer keeps access while billing is unreachable.
 * Long enough to ride out a serious outage; short enough that a lapsed subscription
 * stops working within a day.
 */
export const SUBSCRIPTION_GRACE_MS = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SubscriptionGraceService {
    private readonly ownedCache = inject(OwnedCacheService);

    private key(companyId: string): string {
        return `ag_subscription_grace_${companyId}`;
    }

    /**
     * Record the outcome of a SUCCESSFUL subscription read. Recording the negative
     * case matters as much as the positive one: once a subscription is seen as
     * canceled, that becomes the last known good, so a later stall fails closed.
     */
    record(companyId: string, canAccessPlatform: boolean, status: string): void {
        if (!companyId) {
            return;
        }
        this.ownedCache.write<SubscriptionGraceRecord>(this.key(companyId), {
            canAccessPlatform,
            status,
            companyId
        });
    }

    /**
     * Whether this user may proceed on the strength of a previous successful read.
     *
     * True only when we positively saw them entitled, recently, for THIS company.
     * Absence of a record — a brand-new user, a cleared browser, a different user on
     * the same machine — returns false, which is the safe direction.
     */
    mayProceedOnGrace(companyId: string): boolean {
        if (!companyId) {
            return false;
        }

        const record = this.ownedCache.read<SubscriptionGraceRecord>(
            this.key(companyId),
            SUBSCRIPTION_GRACE_MS
        );

        return record?.canAccessPlatform === true && record.companyId === companyId;
    }

    /** Drop the record for a company (e.g. on sign-out). */
    clear(companyId: string): void {
        if (companyId) {
            this.ownedCache.remove(this.key(companyId));
        }
    }
}
